import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { PageMeta } from '@/components/seo/PageMeta';
import Footer from '@/components/layout/Footer';
import SearchFilters from '@/components/search/SearchFilters';
import SortSelector from '@/components/search/SortSelector';
import ActivityCard from '@/components/home/ActivityCard';
import VenueCard from '@/components/venue/VenueCard';
import { getCategoryLabel } from '@/services/activityService';
import { searchActivities } from '@/services/searchService';
import { Activity, SearchMetadata } from '@/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { sortActivities, SortOption } from '@/utils/sortingUtils';
import { analytics } from '@/utils/analytics';
import { tiktok } from '@/utils/tiktokPixel';
import { categorySlugMap } from '@/data/categoryPages';

const Search = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [searchMetadata, setSearchMetadata] = useState<SearchMetadata | null>(null);
  const [query, setQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [category, setCategory] = useState('all');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('distance');
  
  // Track the actual searched values (not the input values)
  const [searchedLocation, setSearchedLocation] = useState('');
  const [searchedQuery, setSearchedQuery] = useState('');

  // Parse URL search params on initial load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQuery(params.get('query') || '');
    setLocationFilter(params.get('location') || '');
    setCategory(params.get('category') || 'all');
    const sortParam = params.get('sortBy');
    if (sortParam === 'distance' || sortParam === 'price' || sortParam === 'rating') {
      setSortBy(sortParam);
    }
    
    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      performSearch();
      setLoading(false);
    }, 500);
  }, [location.search]);

  const performSearch = async () => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('query') || '';
    const locationParam = params.get('location') || '';
    const categoryParam = params.get('category') || '';
    
    // Store the searched values
    setSearchedQuery(queryParam);
    setSearchedLocation(locationParam);
    
    const result = await searchActivities(
      queryParam,
      locationParam,
      categoryParam !== 'all' ? categoryParam : ''
    );
    
    setActivities(result.activities);
    setSearchMetadata(result.metadata || null);
    
    // Track search event
    const searchQuery = [queryParam, locationParam, categoryParam].filter(Boolean).join(' ');
    if (searchQuery) {
      analytics.searchPerformed(searchQuery, result.activities.length, {
        category: categoryParam,
        location: locationParam,
        priceRange: `${minPrice}-${maxPrice}`,
      });
      tiktok.searched(searchQuery);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (locationFilter) params.append('location', locationFilter);
    if (category && category !== 'all') params.append('category', category);
    if (sortBy !== 'distance') params.append('sortBy', sortBy);
    
    navigate(`/search?${params.toString()}`);
  };

  const handleSortChange = (newSortBy: SortOption) => {
    setSortBy(newSortBy);
    const params = new URLSearchParams(location.search);
    if (newSortBy !== 'distance') {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  // Apply sorting to activities
  const sortedActivities = sortActivities(activities, sortBy);

  // Group activities by venue (professional_id)
  const venues = useMemo(() => {
    const venueMap = new Map<string, Activity[]>();
    
    sortedActivities.forEach(activity => {
      if (activity.professionalId) {
        const existing = venueMap.get(activity.professionalId) || [];
        venueMap.set(activity.professionalId, [...existing, activity]);
      } else {
        // If no professionalId, treat as individual venue
        venueMap.set(activity.id, [activity]);
      }
    });
    
    return Array.from(venueMap.values());
  }, [sortedActivities]);

  const params = new URLSearchParams(location.search);
  const categoryParam = params.get('category') || 'all';
  const locationParam = params.get('location') || '';
  const searchTitle =
    categoryParam !== 'all'
      ? `${getCategoryLabel(categoryParam)} en Île-de-France`
      : 'Rechercher une activité';
  const searchDescription =
    categoryParam !== 'all'
      ? `Réservez des activités ${getCategoryLabel(categoryParam).toLowerCase()} en Île-de-France. Filtrez par lieu et prix.`
      : 'Réservez bowling, karting, escape game, laser game, padel en Île-de-France. Filtrez par lieu, prix et catégorie.';

  // When only the category filter is active (no location/query/price), use the
  // canonical category landing page URL to avoid duplicate content with /search?category=X
  const hasOtherFilters = locationParam || params.get('q') || params.get('min') || params.get('max');
  const categoryLandingSlug =
    categoryParam !== 'all' && !hasOtherFilters ? categorySlugMap[categoryParam] : undefined;
  const canonicalPath = categoryLandingSlug
    ? `/${categoryLandingSlug}`
    : location.search
    ? `/search${location.search}`
    : '/search';

  // noindex faceted search URLs (query, category, location, sort, price filters)
  // to avoid indexing infinite combinations; only base /search is indexed
  const hasSearchParams = location.search.length > 0;

  return (
    <div className="min-h-screen flex flex-col">
      <PageMeta
        title={searchTitle}
        description={searchDescription}
        canonical={canonicalPath}
        noIndex={hasSearchParams}
      />
      <Navbar />
      
      <main className="flex-1 bg-gray-50 py-8">
        <div className="container">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {category && category !== 'all'
                ? `${getCategoryLabel(category)} en Île-de-France`
                : 'Toutes les activités indoor en Île-de-France'}
            </h1>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm sm:text-base text-gray-600">
                {venues.length} établissement{venues.length > 1 ? 's' : ''} trouvé{venues.length > 1 ? 's' : ''}
                {searchedLocation && ` à ${searchedLocation}`}
                {searchedQuery && ` pour "${searchedQuery}"`}
                {' '}({activities.length} activité{activities.length > 1 ? 's' : ''})
              </p>
              <div className="w-full sm:w-auto">
                <SortSelector value={sortBy} onChange={handleSortChange} />
              </div>
            </div>
          </div>
          
          <SearchFilters
            query={query}
            setQuery={setQuery}
            location={locationFilter}
            setLocation={setLocationFilter}
            category={category}
            setCategory={setCategory}
            minPrice={minPrice}
            setMinPrice={setMinPrice}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            onSearch={handleSearch}
          />
          
          {searchMetadata && searchMetadata.expandedSearch && (
            <Alert className="mb-6 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                Aucune activité trouvée à {searchMetadata.searchLocation}. Voici les activités dans un rayon de {searchMetadata.radiusUsed} km.
              </AlertDescription>
            </Alert>
          )}
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white h-80 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : venues.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues.map((venueActivities, index) => (
                <VenueCard 
                  key={venueActivities[0]?.professionalId || venueActivities[0]?.id || index} 
                  activities={venueActivities} 
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">Aucun résultat trouvé</h3>
              <p className="text-gray-600 mb-6">
                Essayez de modifier vos critères de recherche pour trouver des activités.
              </p>
              <button
                onClick={() => {
                  setQuery('');
                  setLocationFilter('');
                  setCategory('all');
                  navigate('/search');
                }}
                className="text-brand-primary hover:underline"
              >
                Réinitialiser tous les filtres
              </button>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Search;
